import { useEffect, useState } from "react";
import { api, apiErr } from "@/admin/api";
import { PageTitle } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Download, Search, Users, ShieldCheck, Sparkles, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-bone p-5 flex items-center gap-4">
    <div className="h-11 w-11 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center shrink-0"><Icon size={20} /></div>
    <div>
      <p className="text-2xl font-serif font-semibold text-ink leading-none">{value}</p>
      <p className="text-xs text-ink-soft mt-1">{label}</p>
    </div>
  </div>
);

export default function Subscribers() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [interest, setInterest] = useState("all");
  const [source, setSource] = useState("all");
  const [exporting, setExporting] = useState(false);

  const load = async (q = search, i = interest, s = source) => {
    try {
      const params = {};
      if (q) params.search = q;
      if (i && i !== "all") params.interest = i;
      if (s && s !== "all") params.source = s;
      const { data } = await api.get("/admin/subscribers", { params });
      setData(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load("", "all", "all"); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search, interest, source), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, interest, source]);

  const isSegmented = interest !== "all" || source !== "all";

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (interest !== "all") params.interest = interest;
      if (source !== "all") params.source = source;
      const res = await api.get("/admin/subscribers/export", { params, responseType: "blob" });
      const seg = [interest, source].filter((x) => x !== "all").join("_") || "all";
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kisliving_travel_list_${seg}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(isSegmented ? "Segment exported" : "Travel List exported");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setExporting(false); }
  };

  const subs = data?.subscribers || [];
  const stats = data?.stats || { total: 0, consented: 0, new_this_week: 0 };
  const opts = data?.filters || { interests: [], sources: [] };

  return (
    <div data-testid="admin-subscribers">
      <PageTitle title="Travel List" subtitle="Everyone who joined your list — deduped by email, searchable and exportable">
        <Button data-testid="subscribers-export" onClick={exportCsv} disabled={exporting || subs.length === 0}
          className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
          {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
          {isSegmented ? "Export segment" : "Export CSV"}
        </Button>
      </PageTitle>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard testid="stat-total" icon={Users} label="Total subscribers" value={stats.total} />
        <StatCard testid="stat-consented" icon={ShieldCheck} label="Consented to updates" value={stats.consented} />
        <StatCard testid="stat-new" icon={Sparkles} label="New this week" value={stats.new_this_week} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft/50" />
          <Input data-testid="subscribers-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…" className="rounded-full border-bone bg-white pl-9" />
        </div>
        <Select value={interest} onValueChange={setInterest}>
          <SelectTrigger data-testid="filter-interest" className="rounded-full border-bone bg-white w-full sm:w-48"><SelectValue placeholder="Interest" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All interests</SelectItem>
            {opts.interests.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger data-testid="filter-source" className="rounded-full border-bone bg-white w-full sm:w-44"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {opts.sources.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isSegmented && (
        <p data-testid="segment-note" className="text-xs text-ink-soft mb-3">
          Showing <b>{subs.length}</b> subscriber{subs.length === 1 ? "" : "s"} in this segment. Export sends just this segment.
        </p>
      )}

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-terracotta" /></div>
      ) : subs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-bone p-16 text-center">
          <p className="text-ink-soft">{search || isSegmented ? "No subscribers match your filters." : "No subscribers yet."}</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-bone overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-soft border-b border-bone bg-sand/50">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Contact</th>
                  <th className="px-5 py-3 font-medium">Interest</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Consent</th>
                  <th className="px-5 py-3 font-medium">Joined</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.email} data-testid={`subscriber-row-${s.email}`} className="border-b border-bone/60 last:border-0 hover:bg-sand/40">
                    <td className="px-5 py-3 font-medium text-ink whitespace-nowrap">
                      {s.first_name || "—"}
                      {s.submissions > 1 && <span className="ml-2 text-[10px] bg-[#EAE4DA] text-ink-soft px-1.5 py-0.5 rounded-full">{s.submissions}×</span>}
                    </td>
                    <td className="px-5 py-3 text-ink-soft">
                      <a href={`mailto:${s.email}`} className="flex items-center gap-1.5 text-terracotta hover:underline"><Mail size={13} /> {s.email}</a>
                      {s.phone && <span className="flex items-center gap-1.5 mt-0.5 text-xs"><Phone size={12} /> {s.phone}</span>}
                    </td>
                    <td className="px-5 py-3 text-ink-soft whitespace-nowrap">{s.travel_interest || "—"}</td>
                    <td className="px-5 py-3 text-ink-soft capitalize whitespace-nowrap">{s.source || "—"}</td>
                    <td className="px-5 py-3">
                      {s.consent
                        ? <span className="text-[11px] bg-sage/15 text-sage px-2 py-0.5 rounded-full font-medium">Yes</span>
                        : <span className="text-[11px] bg-bone text-ink-soft px-2 py-0.5 rounded-full">No</span>}
                    </td>
                    <td className="px-5 py-3 text-ink-soft whitespace-nowrap">{fmtDate(s.first_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
